import { forwardRef, useImperativeHandle, type ReactNode } from "react";
import { Pencil } from "lucide-react";

import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";
import { cn, REVEAL_ON_ROW_HOVER } from "@/lib/utils";
import { useInlineEdit } from "@/lib/use-inline-edit";

export interface InlineEditNameCellHandle {
  /** Enter edit mode imperatively (e.g. from a row's "Rename" menu item). */
  startEditing: () => void;
}

interface InlineEditNameCellProps {
  value: string;
  onSave: (value: string) => Promise<void>;
  /** Rendered before the name, e.g. a revision badge. */
  prefix?: ReactNode;
  /** Extra classes for the display container, e.g. indentation. */
  className?: string;
  /** Describes the value for accessible labels ("Edit {editLabel}"). */
  editLabel?: string;
}

/**
 * Table-cell name that toggles between a display span (with a hover pencil) and
 * an inline `Input`. Wraps {@link useInlineEdit}: focuses/selects on open, saves
 * on Enter/blur, reverts on Escape or a failed save. Callers keep their own
 * `<TableCell>` wrapper and pass this as its content. Renames are project writes, so viewers who
 * can't edit (reviewers) get plain text and `startEditing` does nothing.
 */
export const InlineEditNameCell = forwardRef<InlineEditNameCellHandle, InlineEditNameCellProps>(
  function InlineEditNameCell({ value, onSave, prefix, className, editLabel = value }, ref) {
    const canEdit = useCanEdit();
    const inlineEdit = useInlineEdit(value, onSave);
    useOnEditAccessLost(() => {
      inlineEdit.setIsEditing(false);
      inlineEdit.setValue(value);
    });

    useImperativeHandle(
      ref,
      () => ({ startEditing: () => { if (canEdit) inlineEdit.setIsEditing(true); } }),
      [canEdit, inlineEdit.setIsEditing]
    );

    if (canEdit && inlineEdit.isEditing) {
      return (
        <Input
          ref={inlineEdit.inputRef}
          value={inlineEdit.value}
          onChange={(event) => inlineEdit.setValue(event.target.value)}
          onBlur={() => void inlineEdit.handleSave()}
          onKeyDown={inlineEdit.handleKeyDown}
          className="h-8 w-full max-w-xs"
          aria-label={`Edit ${editLabel}`}
        />
      );
    }

    return (
      <div className={cn("flex items-center gap-2", className)}>
        {prefix}
        <span className="text-foreground">{value}</span>
        {canEdit && (
          <IconButton
            label={`Edit ${editLabel}`}
            icon={Pencil}
            size="xs"
            tooltip="Rename"
            className={REVEAL_ON_ROW_HOVER}
            onClick={() => inlineEdit.setIsEditing(true)}
          />
        )}
      </div>
    );
  }
);
